"use client";

import { useState } from "react";
import {
  LuBuilding,
  LuCheck,
  LuCircleHelp,
  LuClipboardList,
  LuCompass,
  LuCopy,
  LuDownload,
  LuExternalLink,
  LuGlobe,
  LuGraduationCap,
  LuInfo,
  LuLockOpen,
  LuMail,
  LuMessageCircle,
  LuNewspaper,
  LuPlay,
  LuSmartphone,
  LuTarget,
  LuUsers,
  LuX,
} from "react-icons/lu";

// ─── Types ─────────────────────────────────────────────────────────────────

interface CopyBlock {
  label: string;
  text: string;
}

interface ColorSwatch {
  name: string;
  hex: string;
}

// ─── Data ──────────────────────────────────────────────────────────────────

const stats = [
  { value: "1000+", label: "Students Trust Lattu.AI" },
  { value: "18+", label: "Countries covered" },
  { value: "3,000+", label: "Universities" },
  { value: "100+", label: "Scholarships listed" },
  { value: "Web & Android", label: "Live now" },
];

const highlights = [
  {
    icon: LuTarget,
    title: "Built for Bangladesh",
    body: "Designed specifically for the information and trust gaps Bangladeshi students face when planning to study abroad.",
  },
  {
    icon: LuUsers,
    title: "Founded by people who've been there",
    body: "The team went through the study abroad process personally. Every feature is shaped by lived experience, not assumption.",
  },
  {
    icon: LuLockOpen,
    title: "Free at the core",
    body: "The essential tools: journey planner, country explorer, scholarship hub, community are free, always.",
  },
  {
    icon: LuSmartphone,
    title: "Available on Android",
    body: "iOS version coming soon.",
  },
];

const CF = "https://d27uuzzuinwnwc.cloudfront.net/media-kit";

const primaryColors: ColorSwatch[] = [
  { name: "Ink", hex: "#000000" },
  { name: "Pure", hex: "#ffffff" },
  { name: "Lattu", hex: "#615fff" },
];

const secondaryColors: ColorSwatch[] = [
  { name: "Abyss", hex: "#1a1a1a" },
  { name: "Mist", hex: "#eceff1" },
  { name: "Indigo", hex: "#4f39f6" },
  { name: "Iris", hex: "#c7d2ff" },
];

const desktopScreenshots = [
  {
    title: "Journey Dashboard",
    file: "Lattu%20AI%20Journey%20Dashboard%20Screenshot.png",
    size: "1.9 MB",
  },
  {
    title: "Country Explorer",
    file: "Lattu%20AI%20Country%20Explorer%20Screenshot.png",
    size: "1.3 MB",
  },
  {
    title: "Scholarships Page",
    file: "Lattu%20AI%20scholarships%20page%20screenshot.png",
    size: "4.0 MB",
  },
  {
    title: "Community Page",
    file: "Lattu%20AI%20Community%20Page%20Screenshot.png",
    size: "1.8 MB",
  },
  {
    title: "News Section",
    file: "Lattu%20AI%20News%20Section%20Screenshot.png",
    size: "2.4 MB",
  },
];

const mobileScreenshots = [
  {
    title: "Journey",
    file: "Journey_Lattu%20Mobile.jpeg",
    size: "50.6 KB",
  },
  {
    title: "Country Explorer",
    file: "Country%20Explorer_Lattu%20Mobile.jpeg",
    size: "49.4 KB",
  },
  {
    title: "Scholarships",
    file: "Scholarships_Lattu%20Mobile.jpeg",
    size: "52.5 KB",
  },
  {
    title: "Community",
    file: "Community_Lattu%20Mobile.jpeg",
    size: "68.3 KB",
  },
  {
    title: "News",
    file: "News_Lattu%20Mobile.jpeg",
    size: "70.9 KB",
  },
  {
    title: "Dashboard",
    file: "Dashboard_Lattu%20Mobile.jpeg",
    size: "67.8 KB",
  },
];

const copyBlocks: CopyBlock[] = [
  {
    label: "Short Version",
    text: "Lattu AI is a study abroad planning platform built for Bangladeshi students. It helps students turn a confusing journey into a clearer, more personal plan in minutes by bringing together the information, structured guidance, and tools they need to make informed decisions about studying abroad.",
  },
  {
    label: "Medium Version",
    text: "Lattu AI is a study abroad planning platform built for Bangladeshi students. It helps students create a personalized plan based on their goals, budget, background, and preferences, while giving them clearer information on countries, universities, programs, costs, scholarships, visa steps, and student life.\n\nThe platform was built by a Bangladeshi team that has experienced the study abroad journey firsthand and understands how overwhelming the process can feel.",
  },
  {
    label: "Full Version",
    text: "Lattu AI is a study abroad planning platform built for Bangladeshi students.\n\nStudying abroad is one of the biggest decisions a Bangladeshi student can make. The research is scattered, the process is overwhelming, and it is easy to feel like you need to depend on someone else just to figure out where to start. Lattu AI was built to change that.\n\nStudents can use Lattu AI to explore study destinations, understand costs, discover scholarship opportunities, compare options, and create a personalized plan based on their goals, budget, and preferences.\n\nLattu.ai was developed by Xephyr Labs, a custom software development company based in Dhaka, Bangladesh. The founding team members are Bangladeshis who went through the study abroad process themselves and built the tool they wished had existed at the time.",
  },
];

// ─── Sub-components ────────────────────────────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-zinc-600 dark:text-zinc-400 hover:border-[#615fff]/50 hover:text-[#615fff] transition-all shrink-0"
    >
      {copied ? (
        <>
          <LuCheck size={12} />
          Copied
        </>
      ) : (
        <>
          <LuCopy size={12} />
          Copy
        </>
      )}
    </button>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────

export default function PressKitPage() {
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(
    null,
  );

  const handleDownload = (url: string, filename: string) => {
    const apiUrl = `/api/download?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(filename)}`;
    const a = document.createElement("a");
    a.href = apiUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <>
      <div className="w-full bg-transparent scroll-smooth">
        {/* ── Section 1: Hero ─────────────────────────────────────────────── */}
        <section className="relative overflow-hidden pt-20 pb-16 lg:pt-28 lg:pb-20 bg-transparent">
          {/* ambient glow */}
          <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-200 h-100 bg-indigo-500/10 rounded-full blur-[120px]" />

          <div className="relative max-w-5xl mx-auto px-6 text-center">
            {/* Eyebrow + badge row */}
            <div className="flex items-center justify-center gap-3 mb-5">
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-500">
                Media &amp; Press Resources
              </span>
            </div>

            <h1 className="text-4xl lg:text-6xl font-black tracking-tight leading-tight mb-5 text-black dark:text-white">
              Everything you need to cover{" "}
              <span className="text-[#615fff]">Lattu.ai</span>
            </h1>

            <p className="text-lg lg:text-xl max-w-2xl mx-auto leading-relaxed mb-10 text-zinc-600 dark:text-zinc-400">
              Assets, facts, approved copy, and contact information for
              journalists, editors, bloggers, and content creators.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://d27uuzzuinwnwc.cloudfront.net/media-kit/lattu-ai-media-kit.zip"
                download
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-bold text-sm bg-[#615fff] text-white hover:brightness-110 active:scale-95 transition-all shadow-lg shadow-[#615fff]/20"
              >
                <LuDownload size={16} />
                Download Brand Assets
              </a>
              <a
                href="#press-contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-bold text-sm border border-gray-300 dark:border-white/20 text-black dark:text-white hover:border-[#615fff]/50 active:scale-95 transition-all"
              >
                Media Inquiries
              </a>
            </div>
          </div>
        </section>

        {/* ── Section 2: Quick Stats Bar ──────────────────────────────────── */}
        <section className="bg-[#ECEFF1] dark:bg-[#1a1a1a] border-y border-gray-200 dark:border-white/10 transition-colors">
          <div className="max-w-6xl mx-auto px-6 py-8">
            <div className="grid grid-cols-5 divide-x gap-x-16 divide-gray-200 dark:divide-white/10">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="flex flex-col items-center justify-center py-6 px-4 text-center"
                >
                  <span className="text-xl lg:text-3xl font-black mb-1 text-[#615fff] whitespace-nowrap">
                    {stat.value}
                  </span>
                  <span className="text-xs lg:text-sm font-medium text-zinc-600 dark:text-zinc-400">
                    {stat.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Section 3: About Lattu.ai ────────────────────────────────────── */}
        <section className="max-w-5xl mx-auto px-6 py-20 lg:py-24">
          <div className="grid lg:grid-cols-3 gap-14 items-start">
            {/* Left */}
            <div className="space-y-8 lg:col-span-2">
              <div className="space-y-4">
                <span className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-500 block">
                  About Lattu.ai
                </span>
                <h2 className="text-3xl lg:text-4xl font-black tracking-tight text-black dark:text-white">
                  What is Lattu.ai?
                </h2>
                <p className="text-base leading-relaxed text-zinc-600 dark:text-zinc-400">
                  Lattu.ai is a free study abroad planning platform built for
                  Bangladeshi students. Every year, thousands of Bangladeshi
                  students dream of studying abroad. The challenge isn&apos;t
                  ambition. It&apos;s knowing where to start. Lattu AI helps
                  students turn a confusing journey into a clearer, more
                  personal plan by bringing together the information, guidance,
                  and tools they need to understand their options and make
                  informed decisions about their future.
                </p>
              </div>

              <div className="space-y-3">
                <h3 className="text-lg font-black tracking-tight text-black dark:text-white">
                  Why Lattu AI Exists?
                </h3>
                <div className="space-y-3 text-base leading-relaxed text-zinc-600 dark:text-zinc-400">
                  <p>
                    Studying abroad can change a student&apos;s life. But before
                    that happens, students often spend months trying to piece
                    together information from university websites, Facebook
                    groups, YouTube videos, consultants, friends, relatives, and
                    dozens of other sources.
                  </p>
                  <p>
                    Lattu AI was built to bring that information together and
                    help students make sense of it without having to spend hours
                    finding answers.
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-lg font-black tracking-tight text-black dark:text-white">
                  The Problem We&apos;re Trying To Solve
                </h3>
                <ul className="space-y-2">
                  {[
                    "Information scattered across multiple sources",
                    "Confusing and inconsistent advice",
                    "Difficulty comparing countries and options",
                    "Limited awareness of scholarships",
                    "Uncertainty about costs and requirements",
                    "Pressure to make decisions without enough information",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#615fff] shrink-0" />
                      <span className="text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-3">
                <h3 className="text-lg font-black tracking-tight text-black dark:text-white">
                  Who&apos;s behind Lattu AI
                </h3>
                <p className="text-base leading-relaxed text-zinc-600 dark:text-zinc-400">
                  The team behind Lattu AI went through the study abroad process
                  from Bangladesh: the visa anxiety, the financial planning, the
                  uncertainty about whether we were making the right choice. And
                  we came out the other side wishing something like this had
                  existed. So we built it.
                </p>
                <p className="text-base leading-relaxed text-zinc-600 dark:text-zinc-400">
                  Lattu AI is backed by{" "}
                  <strong className="text-black dark:text-white">
                    Xephyr Labs
                  </strong>
                  , a software development company that builds technology
                  products from the ground up.
                </p>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 pt-2">
                <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-indigo-500/10 text-indigo-500 border border-indigo-500/25">
                  Launched 2026
                </span>
                <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/25">
                  Web Platform
                </span>
                <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-[#615fff]/10 text-[#615fff] border border-[#615fff]/25">
                  Free Platform
                </span>
              </div>
            </div>

            {/* Right — 4 highlight cards */}
            <div className="grid grid-cols-1 gap-4">
              {highlights.map((h) => (
                <div
                  key={h.title}
                  className="rounded-2xl p-5 space-y-3 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10"
                >
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-indigo-500/10">
                    <h.icon size={18} className="text-[#615fff]" />
                  </div>
                  <h3 className="font-bold text-sm text-black dark:text-white">
                    {h.title}
                  </h3>
                  {h.title === "Available on Android" && (
                    <a
                      className="max-lg:w-fit flex items-center gap-3 px-5 py-3 bg-black dark:bg-white/10 border border-white/20 opacity-70 rounded-xl hover:scale-105 transition-transform"
                      href="https://play.google.com/store/apps/details?id=com.xephyrlabs.lattuai"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        className="w-6 h-6 fill-white shrink-0"
                      >
                        <path d="M3.18 23.76c.3.17.64.24.99.2l12.6-7.28-2.7-2.7zM.29 1.03C.1 1.35 0 1.73 0 2.15v19.7c0 .42.1.8.29 1.12l.06.06 11.04-11.04v-.26L.35.97zM20.38 10.4l-2.92-1.69-3.03 3.03 3.03 3.03 2.94-1.7c.84-.49.84-1.28-.02-1.67zM4.17.24l12.6 7.28-2.7 2.7z" />
                      </svg>
                      <div className="text-left">
                        <p className="text-white/60 text-[10px] leading-none">
                          Get it on
                        </p>
                        <p className="text-white font-semibold text-sm leading-tight">
                          Google Play
                        </p>
                      </div>
                    </a>
                  )}
                  <p className="text-xs leading-relaxed text-zinc-600 dark:text-zinc-400">
                    {h.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Section 3b: What We Do ──────────────────────────────────────── */}
        <section className="py-20 lg:py-24 bg-[#ECEFF1] dark:bg-[#1a1a1a] transition-colors">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-12 space-y-3">
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-500 block">
                The Platform
              </span>
              <h2 className="text-3xl lg:text-4xl font-black tracking-tight text-black dark:text-white">
                What We Do
              </h2>
              <p className="text-base lg:text-lg max-w-2xl mx-auto leading-relaxed text-zinc-600 dark:text-zinc-400">
                We help students understand their options before they apply and
                help them with the tedious research in minutes that otherwise
                would take hundreds of hours.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                {
                  icon: LuCompass,
                  title: "Create a personalized plan",
                  body: "Students answer a few questions about their goals, budget, academic background, and preferences. Lattu AI then creates a plan that helps them understand which countries, universities, programs, and next steps may make sense for them and get a clearer picture of tuition, living expenses, and other financial considerations.",
                },
                {
                  icon: LuGlobe,
                  title: "Compare countries",
                  body: "If students are not sure where they want to go and where to start, our Country Explorer helps students compare study destinations with information on costs, processes, benefits, risks, and practical tips.",
                },
                {
                  icon: LuGraduationCap,
                  title: "Discover scholarships",
                  body: "The Scholarship Hub brings together scholarship opportunities across different countries, so students do not have to search across dozens of websites.",
                },
                {
                  icon: LuClipboardList,
                  title: "Track your journey",
                  body: "Students usually get lost in this lengthy tedious process. Our application tracker guides students through every stage: preparation, application, acceptance, visa, and arrival.",
                },
                {
                  icon: LuMessageCircle,
                  title: "Learn from community",
                  body: "Lattu Community Forum provides a peer space where students can ask questions, share what they are learning, and connect with others navigating similar decisions.",
                },
                {
                  icon: LuNewspaper,
                  title: "Stay updated",
                  body: "Lattu AI shares relevant updates on visa rules, policy changes, and news that may affect a student's journey.",
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="rounded-2xl p-6 space-y-4 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10"
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#615fff]/10">
                    <item.icon size={20} className="text-[#615fff]" />
                  </div>
                  <h3 className="font-bold text-base text-black dark:text-white">
                    {item.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                    {item.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Section 5: Brand Assets ─────────────────────────────────────── */}
        <section
          id="brand-assets"
          className="max-w-5xl mx-auto px-6 py-20 lg:py-24"
        >
          <div className="text-center mb-12 space-y-3">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-500 block">
              Brand Assets
            </span>
            <h2 className="text-3xl lg:text-4xl font-black tracking-tight text-black dark:text-white">
              Logos &amp; visual identity
            </h2>
            <p className="text-base max-w-xl mx-auto leading-relaxed text-zinc-600 dark:text-zinc-400">
              Use the correct version for your background. Do not alter colors,
              proportions, or typography.
            </p>
          </div>

          {/* Logos */}
          <div className="mb-8">
            <p className="text-xs font-bold uppercase tracking-[0.25em] mb-4 text-zinc-500 dark:text-zinc-500">
              Logos
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {
                  name: "Primary Logo — Dark",
                  file: "Primary%20Logo_Dark.png",
                  bg: "#ffffff",
                },
                {
                  name: "Primary Logo — Light",
                  file: "Primary%20Logo_Light.png",
                  bg: "#1a1a2e",
                },
                {
                  name: "Logomark — Dark",
                  file: "Logomark_Dark.png",
                  bg: "#ffffff",
                },
                {
                  name: "Logomark — Light",
                  file: "Logomark_Light.png",
                  bg: "#1a1a2e",
                },
              ].map((logo) => (
                <div
                  key={logo.name}
                  className="rounded-2xl overflow-hidden border border-gray-200 dark:border-white/10"
                >
                  <div
                    className="h-24 flex items-center justify-center p-4 cursor-zoom-in"
                    style={{ background: logo.bg }}
                    onClick={() =>
                      setLightbox({ src: `${CF}/${logo.file}`, alt: logo.name })
                    }
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element -- remote host not in images config; see next.config */}
                    <img
                      src={`${CF}/${logo.file}`}
                      alt={logo.name}
                      className="max-h-full max-w-full object-contain pointer-events-none"
                    />
                  </div>
                  <div className="p-3 bg-white dark:bg-white/5 flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-xs text-black dark:text-white">
                        {logo.name}
                      </p>
                      <p className="text-xs mt-0.5 text-zinc-500">PNG</p>
                    </div>
                    <button
                      onClick={() =>
                        handleDownload(
                          `${CF}/${logo.file}`,
                          logo.file.replace(/%20/g, " "),
                        )
                      }
                      className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1.5 rounded-lg bg-[#615fff]/10 text-[#615fff] border border-[#615fff]/25 hover:bg-[#615fff]/20 transition-colors"
                    >
                      <LuDownload size={11} />
                      Save
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Videos */}
          <div className="mb-8">
            <p className="text-xs font-bold uppercase tracking-[0.25em] mb-4 text-zinc-500 dark:text-zinc-500">
              Videos
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                {
                  title: "Brand Story / Promo Video",
                  file: "Promo%20Video%201%20%28YouTube%29_v2.mp4",
                  size: "5.8 MB",
                },
                {
                  title: "Short Demo Video",
                  file: "Lattu_AI_short_demo.mp4",
                  size: "22.0 MB",
                },
              ].map((v) => (
                <div
                  key={v.title}
                  className="rounded-2xl overflow-hidden border border-gray-200 dark:border-white/10"
                >
                  <video
                    controls
                    preload="metadata"
                    className="w-full bg-black"
                    style={{ maxHeight: "200px" }}
                  >
                    <source src={`${CF}/${v.file}`} type="video/mp4" />
                  </video>
                  <div className="p-4 flex items-center justify-between gap-3 bg-white dark:bg-white/5">
                    <div>
                      <p className="font-semibold text-sm text-black dark:text-white">
                        {v.title}
                      </p>
                      <p className="text-xs mt-0.5 text-zinc-500">
                        {v.size} · MP4
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        handleDownload(`${CF}/${v.file}`, v.title + ".mp4")
                      }
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-[#615fff]/10 text-[#615fff] border border-[#615fff]/25 hover:bg-[#615fff]/20 transition-colors shrink-0"
                    >
                      <LuDownload size={12} />
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Product Screenshots — Desktop */}
          <div className="mb-8">
            <p className="text-xs font-bold uppercase tracking-[0.25em] mb-4 text-zinc-500 dark:text-zinc-500">
              Product Screenshots — Desktop
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {desktopScreenshots.map((s) => (
                <div
                  key={s.title}
                  className="rounded-2xl overflow-hidden border border-gray-200 dark:border-white/10"
                >
                  {/* eslint-disable-next-line @next/next/no-img-element -- remote host not in images config; see next.config */}
                  <img
                    src={`${CF}/${s.file}`}
                    alt={s.title}
                    className="w-full h-32 object-cover object-top cursor-zoom-in"
                    onClick={() =>
                      setLightbox({ src: `${CF}/${s.file}`, alt: s.title })
                    }
                  />
                  <div className="p-3 bg-white dark:bg-white/5 space-y-2">
                    <p className="font-semibold text-xs text-black dark:text-white">
                      {s.title}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-zinc-500">{s.size}</span>
                      <button
                        onClick={() =>
                          handleDownload(`${CF}/${s.file}`, s.title + ".png")
                        }
                        className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg bg-[#615fff]/10 text-[#615fff] hover:bg-[#615fff]/20 transition-colors"
                      >
                        <LuDownload size={11} />
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Product Screenshots — Mobile */}
          <div className="mb-8">
            <p className="text-xs font-bold uppercase tracking-[0.25em] mb-4 text-zinc-500 dark:text-zinc-500">
              Product Screenshots — Mobile
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {mobileScreenshots.map((s) => (
                <div
                  key={s.title}
                  className="rounded-2xl overflow-hidden border border-gray-200 dark:border-white/10"
                >
                  {/* eslint-disable-next-line @next/next/no-img-element -- remote host not in images config; see next.config */}
                  <img
                    src={`${CF}/${s.file}`}
                    alt={s.title}
                    className="w-full h-32 object-cover object-top cursor-zoom-in"
                    onClick={() =>
                      setLightbox({ src: `${CF}/${s.file}`, alt: s.title })
                    }
                  />
                  <div className="p-3 bg-white dark:bg-white/5 space-y-2">
                    <p className="font-semibold text-xs text-black dark:text-white">
                      {s.title}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-zinc-500">{s.size}</span>
                      <button
                        onClick={() =>
                          handleDownload(`${CF}/${s.file}`, s.title + ".jpeg")
                        }
                        className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg bg-[#615fff]/10 text-[#615fff] hover:bg-[#615fff]/20 transition-colors"
                      >
                        <LuDownload size={11} />
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile App Mockups */}
          <div className="mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.25em] mb-4 text-zinc-500 dark:text-zinc-500">
              Mobile App Mockups
            </p>
            <div className="grid grid-cols-4 sm:grid-cols-8 gap-3">
              {[2, 3, 4, 5, 6, 7, 8].map((n) => (
                <div
                  key={n}
                  className="rounded-xl overflow-hidden border border-gray-200 dark:border-white/10"
                >
                  {/* eslint-disable-next-line @next/next/no-img-element -- remote host not in images config; see next.config */}
                  <img
                    src={`${CF}/Phone%20Screen%20${n}.jpg`}
                    alt={`Phone Screen ${n}`}
                    className="w-full aspect-9/19 object-cover object-top cursor-zoom-in"
                    onClick={() =>
                      setLightbox({
                        src: `${CF}/Phone%20Screen%20${n}.jpg`,
                        alt: `Phone Screen ${n}`,
                      })
                    }
                  />
                  <div className="p-2 bg-white dark:bg-white/5 flex items-center justify-between">
                    <span className="text-xs text-zinc-500">Screen {n}</span>
                    <button
                      onClick={() =>
                        handleDownload(
                          `${CF}/Phone%20Screen%20${n}.jpg`,
                          `Phone Screen ${n}.jpg`,
                        )
                      }
                      className="text-[#615fff] hover:text-[#615fff]/70 transition-colors"
                    >
                      <LuDownload size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 5b — Color swatches */}
          <div className="rounded-2xl p-6 mb-6 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 space-y-8">
            {/* Primary Colors */}
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] mb-2 text-zinc-500 dark:text-zinc-500">
                Primary Colors
              </p>
              <p className="text-sm mb-5 text-zinc-600 dark:text-zinc-400">
                Three core anchors that define the Lattu AI visual identity.
              </p>
              <div className="flex flex-wrap gap-4">
                {primaryColors.map((swatch) => (
                  <div
                    key={swatch.hex}
                    className="flex flex-col items-center gap-2"
                  >
                    <div
                      className="w-14 h-14 rounded-xl shadow-inner border border-black/10 dark:border-white/10"
                      style={{ background: swatch.hex }}
                    />
                    <div className="text-center">
                      <p className="text-xs font-semibold text-black dark:text-white">
                        {swatch.name}
                      </p>
                      <p className="text-xs font-mono text-zinc-500 dark:text-zinc-500">
                        {swatch.hex}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Secondary Colors */}
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] mb-2 text-zinc-500 dark:text-zinc-500">
                Secondary Colors
              </p>
              <p className="text-sm mb-5 text-zinc-600 dark:text-zinc-400">
                Shades and tints of the primaries. Use for backgrounds,
                overlays, and supporting elements.
              </p>
              <div className="flex flex-wrap gap-4">
                {secondaryColors.map((swatch) => (
                  <div
                    key={swatch.hex}
                    className="flex flex-col items-center gap-2"
                  >
                    <div
                      className="w-14 h-14 rounded-xl shadow-inner border border-black/10 dark:border-white/10"
                      style={{ background: swatch.hex }}
                    />
                    <div className="text-center">
                      <p className="text-xs font-semibold text-black dark:text-white">
                        {swatch.name}
                      </p>
                      <p className="text-xs font-mono text-zinc-500 dark:text-zinc-500">
                        {swatch.hex}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 5c — Typography */}
          <div className="rounded-2xl p-6 mb-6 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10">
            <p className="text-xs font-bold uppercase tracking-[0.25em] mb-5 text-zinc-500 dark:text-zinc-500">
              Typography
            </p>
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5">
                <p className="text-sm font-semibold text-black dark:text-white">
                  Inter
                </p>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  Font Family
                </p>
              </div>
              <a
                href="https://fonts.google.com/specimen/Inter"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-[#615fff]/10 text-[#615fff] border border-[#615fff]/25 hover:bg-[#615fff]/20 transition-colors shrink-0"
              >
                <LuDownload size={13} />
                Download Inter
              </a>
            </div>
          </div>

          {/* 5d — Usage rules */}
          <div className="rounded-2xl p-6 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10">
            <p className="text-xs font-bold uppercase tracking-[0.25em] mb-5 text-zinc-500 dark:text-zinc-500">
              Usage Rules
            </p>
            <div className="grid sm:grid-cols-2 gap-8">
              {/* Do */}
              <div className="space-y-3">
                <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                  Do
                </p>
                {[
                  "Use the approved logo files.",
                  "Keep enough spacing around the logo.",
                  "Use the brand name as Lattu AI or Lattu.ai.",
                  "Use the approved boilerplate when describing the company.",
                  "Contact us if you need a custom image, founder quote, or product screenshot.",
                ].map((rule) => (
                  <div key={rule} className="flex items-start gap-2.5">
                    <LuCheck
                      size={14}
                      className="mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400"
                    />
                    <p className="text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                      {rule}
                    </p>
                  </div>
                ))}
              </div>
              {/* Don't */}
              <div className="space-y-3">
                <p className="text-sm font-bold text-red-500 dark:text-red-400">
                  {"Don't"}
                </p>
                {[
                  "Don't call Lattu AI an immigration agency.",
                  "Don't say Lattu AI applies to universities or visas on behalf of students.",
                  "Don't describe Lattu AI as replacing official advice or legal guidance.",
                  "Don't stretch, recolor, or edit the logo.",
                  "Don't use outdated screenshots.",
                ].map((rule) => (
                  <div key={rule} className="flex items-start gap-2.5">
                    <LuX
                      size={14}
                      className="mt-0.5 shrink-0 text-red-500 dark:text-red-400"
                    />
                    <p className="text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                      {rule}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Download All CTA Banner ─────────────────────────────────────── */}
        <div className="max-w-5xl mx-auto px-6 pb-10">
          <div className="rounded-2xl p-7 lg:p-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 bg-linear-to-br from-indigo-600 via-indigo-700 to-purple-800 dark:from-indigo-900 dark:via-indigo-950 dark:to-purple-950">
            <div className="space-y-1.5 max-w-lg">
              <h3 className="text-xl font-black tracking-tight text-white">
                Download the full press kit
              </h3>
              <p className="text-sm leading-relaxed text-indigo-100">
                Logos (SVG + PNG), brand guidelines, and approved copy —
                packaged in one zip file.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <a
                href="https://d27uuzzuinwnwc.cloudfront.net/media-kit/lattu-ai-media-kit.zip"
                download
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm bg-white text-indigo-700 hover:bg-indigo-50 active:scale-95 transition-all shadow-md"
              >
                <LuDownload size={15} />
                Download Kit (.zip)
              </a>
              <a
                href="#press-contact"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold text-sm border border-white/30 text-white hover:bg-white/10 active:scale-95 transition-all"
              >
                Media Inquiries
              </a>
            </div>
          </div>
        </div>

        {/* ── Additional Resources ────────────────────────────────────────── */}
        <section className="max-w-5xl mx-auto px-6 py-16">
          <div className="text-center mb-10 space-y-3">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-500 block">
              Additional Resources
            </span>
            <h2 className="text-3xl lg:text-4xl font-black tracking-tight text-black dark:text-white">
              Additional Resources
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              {
                icon: LuInfo,
                title: "How Lattu Works",
                description:
                  "A full walkthrough of the platform and how students use it.",
                href: "https://www.lattu.ai/how-it-works/",
              },
              {
                icon: LuPlay,
                title: "Full Product Tutorial",
                description:
                  "A video tour of every feature from start to finish.",
                href: "https://youtu.be/3tKkoVQRUSw?si=nw__e_jP4fBq0XRe",
              },
              {
                icon: LuCircleHelp,
                title: "Product FAQ",
                description:
                  "Answers to the most common questions about Lattu AI.",
                href: "https://www.lattu.ai/FAQ/",
              },
            ].map((resource) => (
              <a
                key={resource.title}
                href={resource.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-2xl p-6 space-y-4 transition-all hover:scale-[1.02] bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-[#615fff]/40 dark:hover:border-[#615fff]/40"
              >
                <div className="flex items-start justify-between">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#615fff]/10">
                    <resource.icon size={20} className="text-[#615fff]" />
                  </div>
                  <LuExternalLink
                    size={14}
                    className="text-zinc-400 group-hover:text-[#615fff] transition-colors mt-1"
                  />
                </div>
                <div>
                  <p className="font-bold text-sm text-black dark:text-white">
                    {resource.title}
                  </p>
                  <p className="text-xs mt-1.5 leading-relaxed text-zinc-600 dark:text-zinc-400">
                    {resource.description}
                  </p>
                </div>
              </a>
            ))}
          </div>
        </section>

        {/* ── Section 6: Boilerplate Copy ─────────────────────────────────── */}
        <section className="py-20 lg:py-24 bg-[#ECEFF1] dark:bg-[#1a1a1a] transition-colors">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-12 space-y-3">
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-500 block">
                Boilerplate Copy
              </span>
              <h2 className="text-3xl lg:text-4xl font-black tracking-tight text-black dark:text-white">
                About Lattu.ai - for press use
              </h2>
              <p className="text-base max-w-2xl mx-auto leading-relaxed text-zinc-600 dark:text-zinc-400">
                Use this approved copy in articles, press releases, and media
                mentions. You can shorten it; please do not reword the core
                description.
              </p>
            </div>

            <div className="space-y-6">
              {copyBlocks.map((block) => (
                <div key={block.label}>
                  <span className="text-xs font-bold uppercase tracking-[0.25em] block mb-3 text-[#615fff]">
                    {block.label}
                  </span>
                  <div className="rounded-2xl p-5 flex items-start justify-between gap-4 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10">
                    <div className="flex-1 space-y-3">
                      {block.text.split("\n\n").map((para, i) => (
                        <p
                          key={i}
                          className="text-sm leading-relaxed text-zinc-600 dark:text-zinc-400"
                        >
                          {para}
                        </p>
                      ))}
                    </div>
                    <CopyButton text={block.text} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Section 7: Press Contact ─────────────────────────────────────── */}
        <section
          id="press-contact"
          className="max-w-5xl mx-auto px-6 py-20 lg:py-24"
        >
          <div className="text-center mb-12 space-y-3">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-500 block">
              Press Contact
            </span>
            <h2 className="text-3xl lg:text-4xl font-black tracking-tight text-black dark:text-white">
              Get in touch
            </h2>
            <p className="text-base max-w-xl mx-auto leading-relaxed text-zinc-600 dark:text-zinc-400">
              For media inquiries, interview requests, fact-checking, or
              permissions. We aim to respond within 24 hours.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {/* Press Email */}
            <a
              href="mailto:connect@xephyrlabs.com"
              className="group rounded-2xl p-6 space-y-4 transition-all hover:scale-[1.02] bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-[#615fff]/40 dark:hover:border-[#615fff]/40"
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#615fff]/10">
                <LuMail size={20} className="text-[#615fff]" />
              </div>
              <div>
                <p className="font-bold text-sm text-black dark:text-white">
                  Contact Us
                </p>
                <p className="text-sm mt-0.5 text-[#615fff]">media@lattu.ai</p>
              </div>
              <p className="text-xs leading-relaxed text-zinc-600 dark:text-zinc-400">
                Media inquiries &amp; interview requests
              </p>
            </a>

            {/* Website */}
            <a
              href="https://www.lattu.ai/"
              target="_blank"
              rel="noopener noreferrer"
              className="group rounded-2xl p-6 space-y-4 transition-all hover:scale-[1.02] bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-indigo-400/40 dark:hover:border-indigo-500/40"
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-indigo-500/10">
                <LuGlobe size={20} className="text-indigo-500" />
              </div>
              <div>
                <p className="font-bold text-sm text-black dark:text-white">
                  Website
                </p>
                <p className="text-sm mt-0.5 text-indigo-500">lattu.ai</p>
              </div>
              <p className="text-xs leading-relaxed text-zinc-600 dark:text-zinc-400">
                Platform &amp; company info
              </p>
            </a>

            {/* Parent Company */}
            <a
              href="https://xephyrlabs.com"
              target="_blank"
              rel="noopener noreferrer"
              className="group rounded-2xl p-6 space-y-4 transition-all hover:scale-[1.02] bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-[#615fff]/40 dark:hover:border-[#615fff]/40"
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#615fff]/10">
                <LuBuilding size={20} className="text-[#615fff]" />
              </div>
              <div>
                <p className="font-bold text-sm text-black dark:text-white">
                  Parent Company
                </p>
                <p className="text-sm mt-0.5 text-[#615fff]">Xephyr Labs</p>
              </div>
              <p className="text-xs leading-relaxed text-zinc-600 dark:text-zinc-400">
                xephyrlabs.com · Dhaka, Bangladesh
              </p>
            </a>
          </div>
        </section>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            onClick={() => setLightbox(null)}
          >
            <LuX size={20} />
          </button>
          {/* eslint-disable-next-line @next/next/no-img-element -- remote host not in images config; see next.config */}
          <img
            src={lightbox.src}
            alt={lightbox.alt}
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
